import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SubpageHeader } from '@/components/ui/SubpageHeader';

const READ_SECONDS = 5;

/**
 * User agreement shown before registration.
 * Replace with counsel-approved copy before production if required.
 */
export default function TermsOfServicePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shareRef = searchParams.get('ref')?.trim() ?? '';

  const [secondsLeft, setSecondsLeft] = useState(READ_SECONDS);
  const [canAgree, setCanAgree] = useState(false);

  useEffect(() => {
    if (secondsLeft <= 0) {
      setCanAgree(true);
      return;
    }
    const id = window.setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [secondsLeft]);

  const registerPath = (() => {
    if (!shareRef) return '/register';
    return `/register?ref=${encodeURIComponent(shareRef)}`;
  })();

  const handleAgree = () => {
    if (!canAgree) return;
    navigate(registerPath, { state: { acceptedTerms: true } });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SubpageHeader
        title="User Agreement"
        subtitle="Please read before registering"
        onBack={() => navigate(-1)}
      />

      <main className="flex-1 px-4 py-6 pb-32 max-w-2xl w-full mx-auto">
        <p className="text-xs text-gray-500 mb-6">Last updated: 15 May 2026</p>

        <article className="max-w-none space-y-6 text-sm text-gray-700 leading-relaxed">
          <section>
            <h2 className="text-base font-bold text-gray-900 mt-0">1. Introduction</h2>
            <p>
              Welcome to finfins (&quot;we&quot;, &quot;us&quot;, or &quot;the Service&quot;). This User Agreement
              (&quot;Agreement&quot;) governs your access to and use of our website, applications, and related services.
              By creating an account or using the Service, you agree to be bound by this Agreement. If you do not agree,
              do not register or use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-base font-bold text-gray-900">2. Eligibility and accounts</h2>
            <p>
              You must provide accurate registration information, including a valid email address. You are responsible for
              maintaining the confidentiality of your credentials and for all activity under your account. Notify us
              promptly of any unauthorised use. We may suspend or terminate accounts that violate this Agreement or pose a
              security risk.
            </p>
          </section>

          <section>
            <h2 className="text-base font-bold text-gray-900">3. Acceptable use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>use the Service for any unlawful purpose or in violation of applicable laws;</li>
              <li>attempt to gain unauthorised access to systems, data, or other users&apos; accounts;</li>
              <li>interfere with or disrupt the integrity or performance of the Service;</li>
              <li>upload malware, spam, or misleading content;</li>
              <li>reverse engineer or attempt to extract source code except where permitted by law.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-bold text-gray-900">4. Orders, billing, and third parties</h2>
            <p>
              Features related to orders, teams, or payments may be subject to additional terms shown at the point of use.
              You are responsible for fees incurred through your account where applicable. Integrations with third-party
              services are governed by those providers&apos; terms as well.
            </p>
          </section>

          <section>
            <h2 className="text-base font-bold text-gray-900">5. Intellectual property</h2>
            <p>
              The Service, including its design, text, graphics, and software, is owned by us or our licensors. We grant
              you a limited, non-exclusive, non-transferable licence to use the Service in accordance with this Agreement.
              You retain ownership of content you submit; you grant us a licence to host and process such content as
              needed to operate the Service.
            </p>
          </section>

          <section>
            <h2 className="text-base font-bold text-gray-900">6. Disclaimer and limitation of liability</h2>
            <p>
              The Service is provided &quot;as is&quot; without warranties of any kind, to the fullest extent permitted by
              law. We do not guarantee uninterrupted or error-free operation. To the extent permitted by law, we are not
              liable for indirect, incidental, special, consequential, or punitive damages, or for loss of profits, data,
              or goodwill, arising from your use of the Service.
            </p>
          </section>

          <section>
            <h2 className="text-base font-bold text-gray-900">7. Changes</h2>
            <p>
              We may modify this Agreement from time to time. We will indicate the updated date at the top of this page
              where practicable. Continued use of the Service after changes constitutes acceptance of the revised terms.
            </p>
          </section>

          <section>
            <h2 className="text-base font-bold text-gray-900">8. Termination</h2>
            <p>
              You may stop using the Service at any time. We may suspend or terminate access for breach of this Agreement
              or for operational, legal, or security reasons. Provisions that by their nature should survive will survive
              termination.
            </p>
          </section>

          <section>
            <h2 className="text-base font-bold text-gray-900">9. Governing law</h2>
            <p>
              This Agreement is governed by the laws applicable in Australia, without regard to conflict-of-law principles,
              unless mandatory consumer protections in your jurisdiction provide otherwise.
            </p>
          </section>

          <section>
            <h2 className="text-base font-bold text-gray-900">10. Contact</h2>
            <p>
              For questions about this Agreement, please use the contact or support channels provided within the Service
              or on our website.
            </p>
          </section>
        </article>
      </main>

      <footer className="sticky bottom-0 z-10 border-t border-gray-200 bg-white/95 backdrop-blur px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="max-w-2xl w-full mx-auto space-y-2">
          {!canAgree && (
            <p className="text-center text-xs text-gray-500">
              Please keep reading. You can agree in {secondsLeft}s.
            </p>
          )}
          <button
            type="button"
            onClick={handleAgree}
            disabled={!canAgree}
            className="w-full h-11 bg-green-600 hover:bg-green-700 active:bg-green-800 disabled:bg-gray-300 disabled:hover:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-normal rounded-lg transition cursor-pointer flex items-center justify-center"
          >
            I have read and agree to the User Agreement
          </button>
        </div>
      </footer>
    </div>
  );
}
